import {useEffect, useState} from 'react'
import {CircularProgress, Grid, FormControl, Paper} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import PopulationDistribution from './cards/PopulationDistribution'
import equalizerIcon from '../../icons/chatbot/equalizer.svg'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import UniqueAddresses from './cards/UniqueAddresses'
import AffectedPopulation from './cards/AffectedPopulation'
import {
  MainBox,
  StyledStatsBox,
  StyledLoadingContainer,
  StyledGrid,
  StyledDiv,
  StyledTypography,
  UniqueHouseholdsCircle,
  CircleAndTextContainer,
  StyledMapFooterBox,
  ButtonContainer,
  StyledButton,
  UniqueHouseholdsCircleOutreachSimulation,
  StyledSpaceBox,
  StyledSwitch,
  PersonasTooltip,
  StyledBadge,
  StyledPersonaToggleText,
  CustomAvatar,
  StickyToggleButton,
  StyledAlert,
  StyledRightButtonsContainer,
  PersonasPlaceholder
} from './DashboardStyle'
import {useFilters} from '../../store/hooks/filters'
import {usePopulationAggregation} from '../../store/hooks/populationAggregation'
import {useNavigate} from 'react-router-dom'
import {useStatesAndHealthConditions} from '../../store/hooks/breadCrumbs'
import {selectFilterDetailsLoading} from '../../store/slices/breadCrumbsSlice'
import {selectCustomizations} from '../../store/slices/filterManagementSlice'
import {useSelector} from 'react-redux'
import {useTotalUSPopulation} from '../../store/hooks/totalUSPopulation'
import loadingTitles from '../../appConfig/loadingTitles.config.json'
import PopulationDensitySingleTile from './cards/PopulationDensitySingleTile'
import {useDistributionDimension} from '../../store/hooks/populationDistributionDimension'
import {usePermission} from '../../store/hooks/permission'
import PropTypes from 'prop-types'
import {useTableViewContext} from '../../store/hooks/tableView'
import NotificationBanner from '../common/NotificationBanner/NotificationBanner'
import getViewerNotification from './api/getViewerNotification'
import dayjs from 'dayjs'
import {useNotificationBanner} from '../../store/hooks/notificationBanner'
import ToggleButtonGroup from '../common/ToggleButtonGroup/ToggleButtonGroup'
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import {useConfig} from '../../store/hooks/config'
import ColorLegend from './charts/PopulationDensityChart/Legends/ColorLegend'
import {useExport} from '../../store/hooks/export'
import {usePointOfInterest} from '../../store/hooks/pointOfInterest'
import ExportButton from '../ExportButton/ExportButton'
import {useFocusLayer} from '../../store/hooks/focusLayer'
import StateFilterComponent from './StateFilter/StateFilterComponent'
import {getEnrollmentData, getOutreachLegends as getOutreachSimulationLegends, visualOptions} from './helper'
import {FilterAltSharp} from '@mui/icons-material'
import {AnalyticsHandler} from '../../shared/handlers/analytics/handlers'
import {useOutreachFilters} from '../../store/hooks/useOutreachFilters'
import {useFilterManagement} from '../../store/hooks/useFilterManagement'
import UseCase from './UseCase'
import {useRolePermissions} from '../../hooks/useRolesPermissions'

import UseCaseFilterAlert from '../common/UseCaseFilterAlert'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import personasImage from '../../icons/Persona-image.png'
import {usePersona} from '../../store/hooks/persona'
import {usePersonaQuery} from '../../store/hooks/personaQuery'
import {useAnalyticsPageLoad} from '../../hooks/useAnalyticsPageLoad'
import {useUserFeedback} from '../../store/hooks/userFeedback'
import PercentagePopup from './percentagePopup/PercentagePopup'
import {StyledSpaceBox as SpaceBox} from './charts/PopulationDensityChart/Tooltips/Styles'
import {useCustomDashboardViewProvider} from '../../store/hooks/customDashboardView'

export default function DashboardComponent({
  isAdminPanelOpen,
  setIsAdminPanelOpen,
  admin,
  showChoropleth,
  setShowChoropleth
}) {
  const {populationAggregationState, loadingAggregationsState} = usePopulationAggregation()
  const {totalPopulation, loadingTotalPopulation} = useTotalUSPopulation()
  const {showPopulationDistribution, setShowPopulationDistribution} = useDistributionDimension()
  const {permission} = usePermission()
  const dataAggregationValue = permission?.healthEquityConditionList?.dataAggregationPermissions
  const {showPopulationDensityTable, updateTogglePopulationDensity} = useTableViewContext()
  // Note: useMap() cannot be called here as Map context is not available at this level
  // Map ref needs to be passed from child component where Map is rendered
  const data = populationAggregationState?.populationAggregation || {}
  const {population} = totalPopulation?.totalUsPopulation || {}
  const {filtersOpen, setFiltersOpen} = useFilters()
  const {personaSidebarOpen, setPersonaSidebarOpen, personasToggle, setPersonasToggle, personas} = usePersona()
  const {filters} = useStatesAndHealthConditions()
  const filterDetailsLoading = useSelector(selectFilterDetailsLoading)
  const notLoading = !filterDetailsLoading
  const theme = useTheme()
  const {selectedPOI} = usePointOfInterest()
  // New filter management system for outreach
  const {
    isOutreachActive,
    activeOutreachType,
    enrolledValue: benefitsEnrolledOptionValue,
    outreachParams,
    selectedOptions,
    activeFilterOptions
  } = useOutreachFilters()
  const {poiImageData} = useExport()
  const [alertOpen, setAlertOpen] = useState(true)
  const [mapInstance, setMapInstance] = useState(null)
  const {totalAddedFiltersCount, appliedFilters, filterMetadata, filterConfig} = useFilterManagement()
  const customizations = useSelector(selectCustomizations)
  const {isSuperAdmin, isDeliveryAdmin, isCustomerSupport} = useRolePermissions()
  const isPersonaAllowed = permission?.healthEquityConditionList?.personaFlagPermissions?.[0] === 1 || admin
  const isUserfeedbackAllowed = permission?.healthEquityConditionList?.userfeedbackFlagPermissions?.[0] === 1
  const {isRecording} = useUserFeedback()
  const {prismProgram} = useCustomDashboardViewProvider()
  const navigate = useNavigate()

  const handleAlertClose = () => {
    setAlertOpen(false)
  }
  useEffect(() => {
    setIsAdminPanelOpen(false)
  })
  useEffect(() => {
    return () => {
      setPersonaSidebarOpen({...personaSidebarOpen, open: false})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useAnalyticsPageLoad('Point In Time')

  const statesList = filters?.find((value) => value.key === 'state')?.values
  const statesSelected = filters?.find((value) => value.key === 'state')?.default
  const defaultStateAbbreviation = filters?.find((value) => value.key === 'state')?.stateAbbreviation
  const toggleBtnDetails = [
    {
      tooltipProps: {title: 'Map View'},
      toggleBtnProps: {value: 'map', 'aria-label': 'map', icon: <MapOutlinedIcon />}
    },
    {
      tooltipProps: {title: 'Table View'},
      toggleBtnProps: {value: 'table', 'aria-label': 'table', icon: <ViewListOutlinedIcon />}
    }
  ]
  const {statesConfig} = useConfig() || {}
  const {personasConfig} = useConfig() || {}
  const {personaPercentage} = usePersonaQuery()

  const getStateAbbreviation = (selectedState) => {
    if (statesSelected && statesList && statesSelected?.length > 0 && statesList?.length > 0) {
      const getSelectedStatesData = selectedState.map((sateName) => {
        return statesList?.find((obj) => obj.STATE_NAME === sateName)
      })
      return getSelectedStatesData?.map((item) => item.STATE_ABBREVIATION)
    }
  }

  const stateAbbreviation =
    defaultStateAbbreviation?.length > 0 ? defaultStateAbbreviation : getStateAbbreviation(statesSelected)

  const adminmessage = 'You have users with trials expiring soon, visit the Administration page to view.'
  const emailLink = () => {
    return (
      <a href='mailto: ushealthprismsupport@deloitte.com' aria-label='mail-to'>
        ushealthprismsupport@deloitte.com
      </a>
    )
  }

  const [fetchViewerNotification, {data: getViewerNotificationDate}] = getViewerNotification()

  // Use isOutreachActive directly from hook (no useState/useEffect needed)
  const outreachSimulationSelected = isOutreachActive

  const [endDate, setEndDate] = useState(null)
  const {banner, updateBanner} = useNotificationBanner()

  useEffect(() => {
    if (!getViewerNotificationDate) {
      fetchViewerNotification()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getViewerNotificationDate])

  useEffect(() => {
    if (getViewerNotificationDate && getViewerNotificationDate?.expiringUsersList?.UsersDetails.length > 0) {
      setEndDate(getViewerNotificationDate.expiringUsersList.UsersDetails[0]?.END_DATE)
    }
  }, [getViewerNotificationDate])

  const hasDate = endDate !== null
  const formattedDate = dayjs(endDate).format('MM/DD/YY')

  const alertText = hasDate ? (
    <>
      Your trial expires soon on {formattedDate}. Please reach out to {emailLink()} if you’d like to extend your trial
      or purchase a license.
    </>
  ) : (
    // Handle the case of an invalid date (optional)
    <>Unable to determine trial expiry date. Please contact support for assistance.</>
  )
  const handleBannerClose = () => {
    updateBanner(false)
  }

  const {selectedFocusLayer} = useFocusLayer()

  // Here we are getting the data for the pdf export
  const getPopulationForPdf = () => {
    const quickStatData = [{state: 'US', value: population}]
    if (statesSelected?.includes(statesConfig?.entireUnitedStateLabel) && isOutreachActive) {
      const {enrolledCount, notEnrolledCount} = getEnrollmentData(data)
      quickStatData.push({
        state: 'US',
        value: {
          enrolled: enrolledCount,
          notEnrolled: notEnrolledCount,
          enrolledValue: benefitsEnrolledOptionValue,
          outreachSimulationSelected: true
        }
      })
    } else if (
      statesSelected &&
      !statesSelected.includes('No Data') &&
      !statesSelected.includes(statesConfig?.entireUnitedStateLabel)
    ) {
      for (const [index, obj] of statesSelected.entries()) {
        const stateData = data.length > 0 && data.find((dataObj) => dataObj.state === obj)
        quickStatData.push({
          state: stateAbbreviation.length > 0 && stateAbbreviation[index],
          value: isOutreachActive
            ? {
                enrolled: stateData?.enrolled,
                notEnrolled: stateData?.notEnrolled,
                unique: stateData?.count,
                enrolledValue: benefitsEnrolledOptionValue,
                outreachSimulationSelected: true
              }
            : {
                population: stateData?.population,
                unique: stateData?.count,
                enrolledValue: benefitsEnrolledOptionValue
              }
        })
      }
    }

    return quickStatData
  }

  const pdfData = {
    mapRef: mapInstance, // Map instance passed from PopulationDensityChart
    filters, // State and Health Equity from breadcrumbs (unchanged)
    // New filter management data
    appliedFilters,
    filterMetadata,
    filterConfig,
    customizations, // Custom filter names from API
    // Outreach data (using new filter management system)
    outreachData: {
      isActive: isOutreachActive,
      type: activeOutreachType,
      enrolledValue: benefitsEnrolledOptionValue,
      selectedOptions,
      activeFilterOptions,
      params: outreachParams
    },
    quickStatData: getPopulationForPdf(),
    selectedPOI,
    selectedFocusLayer,
    poiImageData,
    personas,
    personasConfig,
    personasToggle,
    personaPercentage,
    prismProgram
  }

  const filterSelectedCount = totalAddedFiltersCount
  const isExportPermissionEnabled = () =>
    permission?.healthEquityConditionList?.exportFlagPermissions?.[0] === 1 || admin

  const getOutreachQuickStats = () => {
    return statesSelected?.includes(statesConfig?.entireUnitedStateLabel) && isOutreachActive ? (
      <StyledGrid>
        {(benefitsEnrolledOptionValue === 2 || benefitsEnrolledOptionValue === 1) && (
          <AffectedPopulation
            state={'US'}
            sampleSize={getEnrollmentData(data)?.enrolledCount}
            loadingAggregations={loadingAggregationsState}
            outreachSimulationTitle={'Total US Enrolled'}
          />
        )}
        {(benefitsEnrolledOptionValue === 2 || benefitsEnrolledOptionValue === 0) && (
          <AffectedPopulation
            state={'US'}
            sampleSize={getEnrollmentData(data)?.notEnrolledCount}
            loadingAggregations={loadingAggregationsState}
            outreachSimulationTitle={'Total US Not Enrolled'}
          />
        )}
      </StyledGrid>
    ) : null
  }

  const getQuickStats = () => {
    return (
      statesSelected &&
      !statesSelected.includes('No Data') &&
      !statesSelected.includes(statesConfig?.entireUnitedStateLabel) &&
      statesSelected.map((obj, index) => {
        const stateData = data.length > 0 && data.find((dataObj) => dataObj.state === obj)
        const stateCode = stateAbbreviation.length > 0 && stateAbbreviation[index]
        return (
          <StyledGrid key={obj}>
            {(benefitsEnrolledOptionValue === 2 || benefitsEnrolledOptionValue === 1) && (
              <AffectedPopulation
                style={{margin: '4px'}}
                state={stateCode}
                sampleSize={outreachSimulationSelected ? stateData?.enrolled : stateData?.population}
                loadingAggregations={loadingAggregationsState}
                outreachSimulationTitle={outreachSimulationSelected ? `Total ${stateCode} Enrolled` : ''}
                percentage={outreachSimulationSelected ? stateData?.usSnapEnrolledPercentage : stateData?.usPercentage}
              />
            )}
            {(benefitsEnrolledOptionValue === 2 || benefitsEnrolledOptionValue === 0) && (
              <UniqueAddresses
                state={stateCode}
                uniqueAddresses={outreachSimulationSelected ? stateData?.notEnrolled : stateData?.count}
                loadingAggregations={loadingAggregationsState}
                outreachSimulationTitle={outreachSimulationSelected ? `Total ${stateCode} Not Enrolled` : ''}
                percentage={outreachSimulationSelected ? stateData?.usSnapNotEnrolledPercentage : undefined}
              />
            )}
            {outreachSimulationSelected && (
              <UniqueAddresses
                state={stateCode}
                uniqueAddresses={stateData?.count}
                loadingAggregations={loadingAggregationsState}
              />
            )}
          </StyledGrid>
        )
      })
    )
  }

  const getLegends = () => {
    return selectedFocusLayer === 'none' ? null : (
      <>
        <UniqueHouseholdsCircle dotColor={theme.palette.hpAccentPink} />
        <StyledTypography variant='subtitle2'>
          {selectedFocusLayer === visualOptions[1].value ? 'Individual Households' : 'Concentration of Households'}
          &nbsp;
        </StyledTypography>
      </>
    )
  }

  const getOutreachLegends = () => {
    return (
      <>
        {selectedFocusLayer === 'none'
          ? null
          : getOutreachSimulationLegends(activeOutreachType, benefitsEnrolledOptionValue, selectedFocusLayer)?.map(
              (legend, index) => {
                return (
                  <CircleAndTextContainer key={legend.type}>
                    {index > 0 && <StyledSpaceBox />}
                    <UniqueHouseholdsCircleOutreachSimulation type={legend.type} />
                    <StyledTypography variant='subtitle2'>{legend.legendText}</StyledTypography>
                  </CircleAndTextContainer>
                )
              }
            )}
      </>
    )
  }

  const handlePersonaToggleChange = () => {
    if (personasToggle && personaSidebarOpen.open) {
      setPersonaSidebarOpen({...personaSidebarOpen, open: !personaSidebarOpen.open})
    }
    !personasToggle && AnalyticsHandler.onPersonaToggle('Persona')
    setPersonasToggle(!personasToggle)
  }

  const getPersonas = () => {
    const tooltipId = 'personas-tooltip'
    const tooltipTitle =
      'Personas help to tailor your outreach and target interventions by better understanding social, environmental, economic, and behavioral influences.'
    return isPersonaAllowed ? (
      <PersonasTooltip title={<span id={tooltipId}>{tooltipTitle}</span>} placement='top-start'>
        <Paper
          elevation={1}
          style={{padding: '4px', borderRadius: '8px', paddingRight: '10px'}}
          tabIndex={0}
          aria-describedby={tooltipId}
        >
          <FormGroup row>
            <CustomAvatar alt='Personas' src={personasImage} />
            <FormControlLabel
              control={
                <StyledSwitch
                  id='persona-toggle-switch'
                  tabIndex={0}
                  checked={personasToggle}
                  onChange={handlePersonaToggleChange}
                  isPersonaAllowed={isPersonaAllowed}
                  aria-label='Toggle'
                />
              }
              label={<StyledPersonaToggleText>Personas | Map View</StyledPersonaToggleText>}
              labelPlacement='start'
            />
          </FormGroup>
        </Paper>
      </PersonasTooltip>
    ) : null
  }

  const getPopulationDistributionButtonText = () => {
    return showPopulationDistribution ? 'Hide Distribution Graph' : 'View Distribution Graph'
  }

  const getPopulationDistributionIcon = () => {
    return showPopulationDistribution ? (
      <ExpandLessIcon className='expand-icon' />
    ) : (
      <ExpandMoreIcon className='expand-icon' />
    )
  }

  const getPopulationDistributionAriaLabel = () => {
    return showPopulationDistribution ? 'Hide Population Distribution' : 'Show Population Distribution'
  }

  const getPopulationDistributionVariant = () => {
    return showPopulationDistribution ? 'hide' : 'show'
  }

  return (
    <MainBox component='main' open={filtersOpen} persona={personaSidebarOpen.open}>
      <PercentagePopup />
      {notLoading ? (
        <>
          {getViewerNotificationDate?.expiringUsersList?.UsersDetails.length > 0 && (
            <NotificationBanner
              isAdminpanelOpen={isAdminPanelOpen}
              message={isSuperAdmin() || isDeliveryAdmin() || isCustomerSupport() ? adminmessage : alertText}
              open={banner}
              handleBannerClose={handleBannerClose}
            />
          )}
          <UseCaseFilterAlert />
          {alertOpen && isUserfeedbackAllowed && (
            <StyledAlert severity='info' onClose={handleAlertClose}>
              <strong>User Feedback mode is On. </strong> To turn it off, please visit the Manage My Permissions section
              of the Admin panel.
              <StyledButton
                disabled={isRecording}
                fontSize={theme.typography.subtitle2.fontSize}
                onClick={() => {
                  navigate(`/adminpanel/manage-my-permissions`)
                }}
              >
                Manage My Permissions
              </StyledButton>
            </StyledAlert>
          )}
          <div>
            <ButtonContainer
              bannerPresent={getViewerNotificationDate?.expiringUsersList?.UsersDetails.length > 0 && banner}
            >
              <FormControl aria-label='state filter'>
                <StateFilterComponent isLoading={notLoading} />
              </FormControl>
              <StyledBadge badgeContent={filterSelectedCount || 0} showZero={false} color='primary'>
                <StyledButton
                  variant='subtitle1'
                  startIcon={<FilterAltSharp sx={{fontSize: 'inherit'}} />}
                  onClick={() => {
                    setFiltersOpen(!filtersOpen)
                  }}
                >
                  Filters
                </StyledButton>
              </StyledBadge>

              <UseCase />
            </ButtonContainer>
            <StyledStatsBox
              spacing={1}
              bannerOpen={getViewerNotificationDate?.expiringUsersList?.UsersDetails.length > 0 && banner}
            >
              <AffectedPopulation state={'US'} sampleSize={population} loadingAggregations={loadingTotalPopulation} />
              {getQuickStats()}
              {getOutreachQuickStats()}
            </StyledStatsBox>
            <StyledDiv showTableView={showPopulationDensityTable}>
              {getPersonas() || <PersonasPlaceholder aria-hidden='true' />}
              <StyledRightButtonsContainer>
                {isExportPermissionEnabled() ? (
                  <ExportButton disabled={!notLoading} pdfData={pdfData} />
                ) : (
                  <span>&nbsp;</span>
                )}
                <ToggleButtonGroup
                  onSelectionSuccess={() => {
                    AnalyticsHandler.onDashboardButtonClick({
                      button: {name: showPopulationDensityTable ? 'Table to Map' : 'Map to Table'}
                    })
                  }}
                  toggleButtons={toggleBtnDetails}
                  onSelection={updateTogglePopulationDensity}
                  defaultValue={showPopulationDensityTable ? 'table' : 'map'}
                  isMapContainer={true}
                />
              </StyledRightButtonsContainer>
            </StyledDiv>

            <Grid container spacing={1}>
              <Grid item xs={12} xl={12}>
                <PopulationDensitySingleTile
                  isAdminPanelOpen={isAdminPanelOpen}
                  showChoropleth={showChoropleth}
                  setShowChoropleth={setShowChoropleth}
                  personasToggle={personasToggle}
                  onMapReady={setMapInstance}
                />
              </Grid>
              <Grid item xs={12} xl={12}>
                <StyledMapFooterBox>
                  {!personasToggle && showChoropleth && <ColorLegend />}
                  {(admin || dataAggregationValue?.includes(1) || dataAggregationValue?.includes(4)) &&
                    !showPopulationDensityTable && (
                      <CircleAndTextContainer tabIndex={0}>
                        {isOutreachActive ? getOutreachLegends() : getLegends()}
                      </CircleAndTextContainer>
                    )}
                </StyledMapFooterBox>
              </Grid>
              <Grid item xs={12} lg={12} xl={12}>
                <StickyToggleButton
                  variant={getPopulationDistributionVariant()}
                  onClick={() => setShowPopulationDistribution(!showPopulationDistribution)}
                  aria-label={getPopulationDistributionAriaLabel()}
                  data-testid='generate-distribution-graph-button'
                >
                  <img src={equalizerIcon} alt='Equalizer Icon' className='equalizer-icon' />
                  {getPopulationDistributionButtonText()}
                  {getPopulationDistributionIcon()}
                </StickyToggleButton>
                {showPopulationDistribution && <PopulationDistribution />}
              </Grid>
            </Grid>
          </div>
          {alertOpen && isUserfeedbackAllowed && <SpaceBox size={80} />}
        </>
      ) : (
        <StyledLoadingContainer>
          <CircularProgress size={50} title={loadingTitles.dashboard} />
        </StyledLoadingContainer>
      )}
    </MainBox>
  )
}

DashboardComponent.propTypes = {
  isAdminPanelOpen: PropTypes.bool,
  setIsAdminPanelOpen: PropTypes.func.isRequired,
  admin: PropTypes.bool,
  showChoropleth: PropTypes.bool,
  setShowChoropleth: PropTypes.func
}

/**
 * DashboardComponent - Main dashboard component for rendering population data, filters, and visualizations.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.isAdminPanelOpen - Indicates if the admin panel is open.
 * @param {Function} props.setIsAdminPanelOpen - Function to toggle admin panel state.
 * @param {boolean} props.admin - Indicates if the user has admin privileges.
 * @param {boolean} props.showChoropleth - Indicates if the choropleth map is displayed.
 * @param {Function} props.setShowChoropleth - Function to toggle choropleth map visibility.
 */

/**
 * getStateAbbreviation - Retrieves state abbreviations for selected states.
 * @param {Array<string>} selectedState - List of selected state names.
 * @returns {Array<string>} List of state abbreviations.
 */

/**
 * emailLink - Generates a mailto link for support email.
 * @returns {JSX.Element} Mailto link element.
 */

/**
 * getPopulationForPdf - Prepares population data for PDF export.
 * @returns {Array<Object>} Data for PDF export.
 */

/**
 * isExportPermissionEnabled - Checks if the user has export permissions.
 * @returns {boolean} True if export is allowed, false otherwise.
 */

/**
 * getOutreachQuickStats - Renders outreach simulation quick stats for the US.
 * @returns {JSX.Element|null} Quick stats component or null.
 */

/**
 * getQuickStats - Renders quick stats for selected states.
 * @returns {Array<JSX.Element>} List of quick stats components.
 */

/**
 * getLegends - Renders legends for the selected focus layer.
 * @returns {JSX.Element|null} Legends component or null.
 */

/**
 * getOutreachLegends - Renders outreach simulation legends.
 * @returns {JSX.Element} Outreach simulation legends component.
 */

/**
 * handlePersonaToggleChange - Toggles the persona view state.
 */

/**
 * getPersonas - Renders the personas toggle UI.
 * @returns {JSX.Element} Personas toggle component.
 */

/**
 * handleBannerClose - Closes the notification banner.
 */
